import { html, View } from 'rune-ts';
import { pipe, map, toArray, find, isEmpty, reduce } from '@fxts/core';
import {
  DropdownItem,
  FormDropdown,
} from '@services/MarppleShop/shared/components/cells/FormDropDown/FormDropdown.ts';
import { FORM_DROPDOWN_CONSTANT } from '@services/MarppleShop/shared/components/cells/FormDropDown/type.ts';
import klass from './Input.module.scss';
import base_klass from '../../../../../shared/component/atom/Input/core/input.module.scss';
import common_klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/styles/common.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import { ValueOf } from '../../../../../../../shared/type/util.type.ts';
import { required_html } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { StoresDeliveryCompanies } from '@services/database/mco/schema/public';
import { StoresDeliveryCompaniesProducts } from '@services/MarppleShop/entities/pool/public/stores_delivery_companies_products.ts';
import { PBDeliveryTrackingConstantS } from '@modules/PB/DeliveryTracking/S/Constant/module/PBDeliveryTrackingConstantS';
import { commify } from '@services/MarppleShop/shared/util/commify.ts';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';

interface InputDeliveryData {
  stores_delivery_companies: Array<Partial<StoresDeliveryCompanies>>;
  stores_delivery_companies_products: Array<
    Pick<StoresDeliveryCompaniesProducts, 'stores_delivery_company_id'>
  >;
  setField: (
    keyName: ValueOf<typeof formKeyMap>,
    value: Array<Pick<StoresDeliveryCompaniesProducts, 'stores_delivery_company_id'>>,
  ) => void;
  status?: keyof typeof FORM_DROPDOWN_CONSTANT;
  is_marppleshop_delivery?: boolean;
}

export class Input_Delivery extends View<InputDeliveryData> {
  private input_delivery_dropdown_status = this.data.status || FORM_DROPDOWN_CONSTANT.ACTIVE;

  private marppleshop_delivery_item = {
    key: '',
    name: '마플샵 배송',
    value: '',
  };

  private delivery_items = this.data.is_marppleshop_delivery
    ? this.makeMarppleShopDeliveryDropDownItem()
    : this.makeDeliveryDropDownItem();

  private deliveryDropDown = new FormDropdown<string>(
    {
      name: formKeyMap.stores_delivery_companies_products,
      status: this.input_delivery_dropdown_status,
      items: this.delivery_items,
      selected_item: this.data.is_marppleshop_delivery
        ? this.marppleshop_delivery_item
        : isEmpty(this.data.stores_delivery_companies_products)
          ? pipe(
              this.delivery_items,
              find((delivery_item) => delivery_item.name === '기본 배송 정책'),
            )
          : pipe(
              this.delivery_items,
              find((delivery_item) =>
                this.data.stores_delivery_companies_products.some(
                  (stores_delivery_companies_product) =>
                    String(stores_delivery_companies_product.stores_delivery_company_id) ===
                    delivery_item.key,
                ),
              ),
            ),
      placeholder: '배송 정책',
    },
    {
      klass: klass.dropdown,
      size: 'small',
      is_mobile: true,
      onSelectHook: (item) => {
        if (item.value) {
          this.setDelivery(item.value);
          this.setDeliveryDescription(item);
        }
      },
    },
  );

  constructor(data: InputDeliveryData) {
    super({ ...data });

    if (this.data.is_marppleshop_delivery) {
      this.data.setField(formKeyMap.stores_delivery_companies_products, []);
    }
  }

  override template() {
    return html`
      <div class="${base_klass.input_root}">
        <div class="${common_klass.title_with_tooltip}">
          <span class="${common_klass.text} ${shareFont('14_bold')}">배송 정책</span>
          ${required_html}
          ${new Tooltip({
            id: formKeyMap.stores_delivery_companies_products,
            content: html`
              <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>상품별로 배송 정책을 다르게 설정할 수 있습니다.</span>
                </div>
                <div class="${common_klass.tooltip_content_divider}">
                  <span>·</span>
                  <span>배송 정책은 내샵 관리 > 배송 정책에서 관리할 수 있습니다.</span>
                </div>
              </div>
            `,
            size: 'x_small',
            variant: 'studio',
          })}
        </div>
        <div class="${klass.dropdown_wrap}">${this.deliveryDropDown}</div>
        <p class="${klass.dropdown_description} ${shareFont('12_medium')}">
          ${this.makeDeliveryDescriptionText(this.deliveryDropDown.getSelectedItem())}
        </p>
      </div>
    `;
  }

  private setDelivery(stores_delivery_company_id: string) {
    const state = this.data.stores_delivery_companies_products.map((stores_delivery_companies_product) => ({
      ...stores_delivery_companies_product,
      stores_delivery_company_id: Number(stores_delivery_company_id),
    }));
    this.data.setField(formKeyMap.stores_delivery_companies_products, state);
    this.data.stores_delivery_companies_products = state;
  }

  private setDeliveryDescription(selected_delivery_item: DropdownItem<string>) {
    const $description = this.element().querySelector(`.${klass.dropdown_description}`);
    if ($description) $description.textContent = this.makeDeliveryDescriptionText(selected_delivery_item);
  }

  private makeDeliveryDropDownItem() {
    return pipe(
      this.data.stores_delivery_companies,
      map((stores_delivery_company) => ({
        key: String(stores_delivery_company.id),
        name: stores_delivery_company?.is_default
          ? stores_delivery_company?.name
            ? String(stores_delivery_company.name)
            : '기본 배송 정책'
          : String(stores_delivery_company.name),
        value: String(stores_delivery_company.id),
      })),
      toArray,
    );
  }

  /**
   * 입고 및 해외 배송은 마플샵 위탁 배송
   */
  private makeMarppleShopDeliveryDropDownItem() {
    return pipe([this.marppleshop_delivery_item], toArray);
  }

  private makeDeliveryDescriptionText(selected_delivery_item: DropdownItem<string>) {
    const selected_stores_delivery_company = pipe(
      this.data.stores_delivery_companies,
      find((stores_delivery_company) => stores_delivery_company.id === Number(selected_delivery_item.value)),
    );

    // 입고 배송 상품일 때
    if (this.data.is_marppleshop_delivery) return `마플샵에 입고된 상품으로 마플샵에서 배송합니다.`;

    // 배송 정책을 등록하지 않았을 때
    if (isEmpty(this.data.stores_delivery_companies)) return ``;
    // 선택된 배송 정책이 존재하지 않을 때
    if (!selected_stores_delivery_company) return ``;

    const {
      delivery_company_id,
      shipping_price,
      free_shipping_min_price,
      is_separate_shipping,
      additional_shipping_price,
      take_back_price,
      exchange_price,
    } = selected_stores_delivery_company;

    return pipe(
      [
        `택배사:
        ${
          Object.entries(PBDeliveryTrackingConstantS.info).find(([, { id }]) => {
            return id == delivery_company_id;
          })?.[1]?.name
        }`,
        `배송비: ${commify(Number(shipping_price))}원`,
        `무료 배송 조건:
        ${
          free_shipping_min_price
            ? `${commify(Number(free_shipping_min_price))}원`
            : T('ecommerce_info::설정 안 함')
        }`,
        `도서 산간 추가 비용: ${commify(Number(additional_shipping_price))}원`,
        `환불 배송비(편도): ${commify(Number(take_back_price))}원`,
        `교환 배송비(왕복): ${commify(Number(exchange_price))}원`,
        `배송지 부과 범위:
        ${
          is_separate_shipping
            ? T('ecommerce_info::개별 상품')
            : T('ecommerce_info::동일 배송 정책을 가진 모든 상품')
        }`,
      ],
      reduce((a, b) => `${a} / ${b}`),
    );
  }
}
